import {
  Box,
  CSSObject,
  IconButton,
  List,
  styled,
  Theme,
  Toolbar,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { FC, useCallback } from 'react';
import {
  MdAccountTree as AccountTree,
  MdApps as Apps,
  MdCampaign as Campaign,
  MdChevronLeft as ChevronLeftIcon,
  MdChevronRight as ChevronRightIcon,
  MdInsertChart as InsertChart,
  MdOutlineGroups as Members,
  MdPolicy as Policy,
  MdSettings as Settings,
  MdTableRows as TableRows,
} from 'react-icons/md';
import { RxActivityLog } from 'react-icons/rx';
import { useSnapshot } from 'valtio';
import { ReactComponent as ExclusionsIcon } from '../../assets/icons/exclusions-small.svg';
import { IconType } from 'common-types';
import CustomTooltip from '../../common/components/CustomTooltip';
import { isDevelopment } from 'env-utils';
import { setOpenDrawer } from '../actions/app-bar-actions';
import { AppPages } from '../../common/routing/app-navigator';
import DrawerStore from '../stores/drawer-store';
import NavItem from './routing/NavItem';
import { GiGlowingArtifact } from 'react-icons/gi';
import { FaInfinity } from 'react-icons/fa';
import { Location, useLocation } from 'react-router';
import { AiOutlineLineChart } from 'react-icons/ai';

export const appNavItems: { href: string; icon: IconType; title: string }[] = [
  {
    href: AppPages.Dashboard,
    icon: InsertChart,
    title: 'Dashboard',
  },
  {
    href: AppPages.Applications,
    icon: TableRows,
    title: 'Applications',
  },
  {
    href: AppPages.Issues,
    icon: Campaign,
    title: 'Issues',
  },

  {
    href: AppPages.PipelineSummary,
    icon: FaInfinity,
    title: 'Pipeline',
  },
  {
    href: AppPages.Connectors,
    icon: Apps,
    title: 'Connectors',
  },
  {
    href: AppPages.Policies,
    icon: Policy,
    title: 'Policies',
  },
  {
    href: AppPages.Exclusions,
    icon: ExclusionsIcon,
    title: 'Exclusions',
  },
  {
    href: AppPages.Sbom,
    icon: AccountTree,
    title: 'SBOM',
  },
  {
    href: AppPages.Artifacts,
    icon: GiGlowingArtifact,
    title: 'Artifacts',
  },
];

const drawerWidth = 140;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  backgroundImage: 'none',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(3)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(7)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: prop => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  height: '100%',
  paddingBottom: theme.spacing(2),
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const isActive = (location: Location, page: string): boolean => {
  return location.pathname.startsWith(page);
};

const AppDrawer: FC<React.PropsWithChildren<unknown>> = () => {
  const { open } = useSnapshot(DrawerStore);
  const location = useLocation();

  const toggle = useCallback(() => {
    setOpenDrawer(!open);
  }, [open]);

  return (
    <Drawer
      variant='permanent'
      sx={{
        flexShrink: 0,
      }}
      PaperProps={{ sx: { backgroundColor: 'transparent' } }}
      open={open}>
      <Toolbar />
      <Box
        display='flex'
        flexDirection='column'
        pb={1}
        width='100%'
        height='100%'>
        <Box flexGrow={1}>
          <List>
            {appNavItems.map(item => (
              <NavItem
                key={item.title}
                href={item.href}
                title={item.title}
                icon={item.icon}
                drawerOpen={open}
                active={isActive(location, item.href)}
              />
            ))}
          </List>
        </Box>
        <List>
          {isDevelopment() && (
            <NavItem
              drawerOpen={open}
              href={AppPages.ScanHistory}
              title='Scan History'
              icon={AiOutlineLineChart}
              active={isActive(location, AppPages.ScanHistory)}
            />
          )}
          <NavItem
            drawerOpen={open}
            href={AppPages.AuditLogs}
            title='Audit Logs'
            icon={RxActivityLog}
            active={isActive(location, AppPages.AuditLogs)}
          />
          <NavItem
            drawerOpen={open}
            href={AppPages.Members}
            title='Members'
            icon={Members}
            active={isActive(location, AppPages.Members)}
          />
          <NavItem
            drawerOpen={open}
            href={AppPages.Settings}
            title='Settings'
            icon={Settings}
            active={isActive(location, AppPages.Settings)}
          />
        </List>
        <CustomTooltip
          title={`${open ? 'Collapse' : 'Expand'} Menu`}
          placement='right'>
          <Box mx={1} alignSelf='flex-end'>
            <IconButton onClick={toggle}>
              {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Box>
        </CustomTooltip>
      </Box>
    </Drawer>
  );
};

export default AppDrawer;
